import { ArrowLeft, ArrowRight } from "@bale/ui";
import { digitsToHindi } from "@bale/ui/lib/utils/numbers";
import { useTheme } from "@emotion/react";
import { Bar, BarChart, ResponsiveContainer, XAxis } from "recharts";
import classNames from "classnames";
import { GetAdJoinReportResponse } from "core/orderAd/orderAd.api";
import { isBaleLink } from "../../../../utils/payment";
import { ApiAdRecord } from "core/orderAd/orderAd.entity";
import { getAdJoinReportThunk } from "core/orderAd/orderAd.thunk";
import { useRequest } from "hooks/useRequest";
import { strings } from "intl/fa";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { addHourToDate, formatMonthAndDayAndHour } from "utils/time";
import { isMobile } from "utils/utils";
import styles from "./chart.module.scss";

interface ChartData {
  date: string;
  join: number;
  left: number;
}

interface CustomBarProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  value?: number;
  color?: string;
}

enum BarType {
  join = "join",
  left = "left",
}

const BarColor = {
  [BarType.join]: "#00B894",
  [BarType.left]: "#FF6B6A",
};
const RoundedTop = (props: CustomBarProps) => {
  const { x = 0, y = 0, width = 100, height = 100, value = "", color } = props;
  return (
    <g>
      <svg x={x} y={y} height={height}>
        <path fill={color} d="M 10 10 H 30 V 1900 H 10 L 10 10" />
        <circle cx="20" cy="10" r="10" fill={color} />
      </svg>

      <text
        x={x + 10 + width / 2}
        y={y - 10}
        fill={color}
        fontWeight={"bolder"}
        textAnchor="middle"
        fontSize={13}
        dominantBaseline="central"
      >
        {digitsToHindi(value.toString())}
      </text>
    </g>
  );
};

const CustomLegend: React.FC<React.PropsWithChildren<{ values: string[] }>> = (props) => {
  const { values } = props;

  const LegendType = {
    join: strings.adChart.joinedMembers,
    left: strings.adChart.leftedMembers,
  };
  // transfrom to div not before
  return (
    <div className={styles.legendContainer}>
      {values.map((entry, index) => (
        <div
          className={classNames(styles.legend, {
            [styles.secondaryLegend]: entry === BarType.left,
          })}
          key={entry}
        >
          <p>{LegendType[entry as BarType]}</p>
        </div>
      ))}
    </div>
  );
};

const SCROLL_STEP = 100;

interface Props {
  adRecord: ApiAdRecord & { bonusView?: number; bonusClick?: number };
}

export const JoinOrLeftChart: React.FC<React.PropsWithChildren<Props>> = ({ adRecord }) => {
  const theme = useTheme();
  const isMobileClient = isMobile();
  const chartContaienrRef = useRef<HTMLDivElement>(null);
  const scrollContaienrRef = useRef<HTMLDivElement>(null);
  const scrollX = useRef<number>(0);
  const leftButtonRef = useRef<HTMLDivElement>(null);
  const rightButtonRef = useRef<HTMLDivElement>(null);
  const lastPoint = useRef<number>();
  const isScrollEnable = useRef({
    right: true,
    left: true,
  });

  const { sendRequest: getAdJoinReport } = useRequest(getAdJoinReportThunk);

  const [report, setReport] = useState<GetAdJoinReportResponse>();

  useEffect(() => {
    if (isBaleLink(adRecord.link)) {
      const response = getAdJoinReport({
        ad_id: adRecord?.id.toString(),
        // ad_id: "9fcdc935-8720-48a4-9c11-7ddc9cfa394a",
        onSuccess: (res) => setReport(res),
      });
    }
  }, [adRecord]);
  const uiData = useMemo<ChartData[]>(() => {
    if (!report || report.join_segments == null) return [];
    return new Array(report.join_segments.length)
      .fill(undefined)
      .map((_, index) => ({
        date: formatMonthAndDayAndHour(
          addHourToDate(new Date(report.start_time).getTime(), index)
        ),
        join: report.join_segments?.[index] || 0,
        left: report.left_segments?.[index] || 0,
      }));
  }, [report]);
  const touchStartHandler = (e: TouchEvent) => {
    const touch = e.touches[0];
    lastPoint.current = touch.clientX;
  };
  const touchMoveHandler = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch.clientX) return;
    scrollX.current = scrollX.current - (lastPoint.current! - touch.clientX);
    lastPoint.current = touch.clientX;
    if (scrollContaienrRef.current)
      scrollContaienrRef.current.style.transform = `translateX(${getScrollXValue()}px)`;
  };

  const getScrollXValue = () =>
    Math.min(
      scrollContaienrRef.current?.clientWidth! -
        chartContaienrRef.current?.clientWidth! +
        SCROLL_STEP,
      Math.max(scrollX.current, -SCROLL_STEP)
    );

  const checkForAvailableScroll = () => {
    if (!chartContaienrRef.current || !scrollContaienrRef.current) return;
    const chartContainerWidth = chartContaienrRef.current.clientWidth;
    const scrollContainerWidth = scrollContaienrRef.current.clientWidth;

    isScrollEnable.current = {
      left: scrollX.current + SCROLL_STEP > 0,
      right: !(
        chartContainerWidth + scrollX.current - SCROLL_STEP >
        scrollContainerWidth
      ),
    };

    if (leftButtonRef.current && rightButtonRef.current) {
      leftButtonRef.current.style.opacity = isScrollEnable.current.left
        ? "1"
        : "0.3";
      leftButtonRef.current.style.cursor = isScrollEnable.current.left
        ? "pointer"
        : "auto";

      rightButtonRef.current.style.opacity = isScrollEnable.current.right
        ? "1"
        : "0.3";
      rightButtonRef.current.style.cursor = isScrollEnable.current.right
        ? "pointer"
        : "auto";
    }
  };

  useEffect(() => {
    chartContaienrRef.current?.addEventListener(
      "touchstart",
      touchStartHandler
    );
    chartContaienrRef.current?.addEventListener("touchmove", touchMoveHandler);
  }, []);

  const handleOnButtonClick = (direction: "left" | "right") => {
    if (direction === "left") {
      if (!isScrollEnable.current.left) return;
      scrollX.current = scrollX.current - SCROLL_STEP;
    } else {
      if (!isScrollEnable.current.right) return;
      scrollX.current = scrollX.current + SCROLL_STEP;
    }
    if (scrollContaienrRef.current)
      scrollContaienrRef.current.style.transform = `translateX(${getScrollXValue()}px)`;
    checkForAvailableScroll();
  };
  if (!report?.join_segments || !report?.join_segments.length) {
    return null;
  }
  return (
    <div className={styles.section}>
      <p className={styles.sectionTitle}>{strings.adChart.adReport}</p>
      <div ref={chartContaienrRef} className={styles.chartContainer}>
        {!isMobileClient && (
          <>
            <div
              ref={rightButtonRef}
              onClick={() => handleOnButtonClick("right")}
              className={styles.arrowButton}
            >
              <ArrowLeft size={24} color={theme.colors.light[0]} />
            </div>
            <div
              ref={leftButtonRef}
              onClick={() => handleOnButtonClick("left")}
              className={classNames(styles.arrowButton, styles.rightArrow)}
            >
              <ArrowRight size={24} color={theme.colors.light[0]} />
            </div>
          </>
        )}
        <p className={styles.chartTitle}>{strings.adChart.membersDiff}</p>
        <CustomLegend values={[...Object.keys(BarType)]} />
        <div
          ref={scrollContaienrRef}
          style={{
            width: uiData.length * 100,
          }}
          className={styles.conatiner}
        >
          <ResponsiveContainer
            style={{ overflowX: "auto", overflowY: "hidden" }}
            width="100%"
            height="100%"
          >
            <BarChart
              margin={{ top: 100 }}
              width={500}
              barCategoryGap={"30%"}
              height={300}
              data={uiData}
            >
              <XAxis dataKey="date" />
              {Object.keys(BarType).map((value) => (
                <Bar
                  dataKey={value}
                  isAnimationActive={false}
                  key={value}
                  activeBar={<RoundedTop color={BarColor[value as BarType]} />}
                  shape={<RoundedTop color={BarColor[value as BarType]} />}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
